import { Link } from "react-router-dom";
import { MaskLines, MaskLinesInView, Reveal } from "../components/Reveal";
import { IMAGES, BRAND } from "../data/brand";
import { useSEO } from "../lib/useSEO";

const TIMELINE = [
  { year: "1980", title: "The Beginning", text: `Homegrown venture founded by ${BRAND.founder} in Bangalore, guided by a belief that every incense stick should create a fragrance worth remembering.` },
  { year: "1990s", title: "Growing With Trust", text: "Expansion of production and fragrance range as SK Fragrances earned the trust of families and retailers across the region." },
  { year: "2000s", title: "New Fragrance Profiles", text: "Traditional craftsmanship combined with modern fragrance preferences, broadening the collection for evolving tastes." },
  { year: "Today", title: "A Trusted Name", text: "A trusted incense manufacturing and retail brand — 41 fragrance expressions across Economy, Premium and Assorted collections." },
];

export default function About() {
  useSEO(
    "About SK Fragrances | Incense Manufacturer Since 1980",
    "Since 1980, SK Fragrances has crafted authentic incense in Bangalore — four decades of craftsmanship, authentic fragrance and everyday affordability."
  );

  return (
    <div data-testid="about-page" className="bg-ivory">
      {/* HERO */}
      <section className="pt-40 md:pt-52 pb-20 md:pb-28 bg-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="text-sm tracking-[0.3em] text-gold mb-6">OUR STORY · SINCE 1980</p>
          <MaskLines lines={["Four Decades.", "One Passion", "for Fragrance."]} className="font-serif text-6xl md:text-8xl leading-[0.95] text-charcoal" />
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-2xl text-charcoal/65 leading-relaxed">
              Since 1980, SK Fragrances has been crafting incense sticks that bring together traditional
              craftsmanship, authentic fragrance and everyday affordability. From a humble Bangalore
              beginning to a trusted fragrance brand, our journey has always been guided by one simple
              belief — good fragrance should be experienced, remembered and shared.
            </p>
          </Reveal>
        </div>
      </section>

      {/* BIG STATEMENT */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-14 items-center">
          <div className="relative overflow-hidden aspect-[4/5]">
            <img src={IMAGES.story} alt="SK Fragrances heritage" className="w-full h-full object-cover" />
          </div>
          <div>
            <span className="font-serif text-8xl md:text-9xl text-gold leading-none">1980</span>
            <p className="mt-4 text-xs tracking-[0.3em] text-charcoal/60">SK FRAGRANCES ESTABLISHED IN BANGALORE</p>
            <div className="mt-8 space-y-5 text-charcoal/70 leading-relaxed">
              <p>What began as a small homegrown workshop has, over four decades, grown into a trusted incense manufacturing brand known for authenticity, affordability and aromatic excellence.</p>
              <p>Every fragrance carries the same intention it did on day one — to bring warmth, calm and character to the spaces people live in.</p>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-24 md:py-32 bg-charcoal text-ivory" data-testid="timeline">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="text-sm tracking-[0.3em] text-gold mb-4">THE JOURNEY</p>
          <MaskLinesInView lines={["A Fragrance Legacy"]} className="font-serif text-5xl md:text-7xl mb-16" />
          <div className="space-y-0">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.08}>
                <div className="grid md:grid-cols-12 gap-4 md:gap-8 py-8 border-t border-ivory/15 group">
                  <div className="md:col-span-3">
                    <span className="font-serif text-4xl md:text-5xl text-gold">{t.year}</span>
                  </div>
                  <div className="md:col-span-9 md:pl-8 md:border-l md:border-ivory/15">
                    <h3 className="font-serif text-3xl text-ivory">{t.title}</h3>
                    <p className="mt-2 text-ivory/65 max-w-2xl">{t.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-burgundy text-ivory text-center">
        <div className="max-w-2xl mx-auto px-5">
          <p className="text-sm tracking-[0.3em] text-gold mb-5">OUR PROMISE</p>
          <MaskLinesInView lines={["The Art of Fragrance,", "Perfected."]} className="font-serif text-4xl md:text-6xl leading-tight" />
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap justify-center gap-5">
              <Link to="/products" data-testid="about-explore" className="btn-line bg-ivory text-charcoal px-9 py-4 text-sm tracking-[0.15em]">EXPLORE THE COLLECTION →</Link>
              <Link to="/contact" className="btn-line border border-ivory/50 text-ivory px-9 py-4 text-sm tracking-[0.15em] hover:text-charcoal">Contact Us</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
